/** 首页 / FAQ 页共用（与 portal-nuxt/utils/marketingFaq.ts 保持同步） */

export const MARKETING_PLATFORMS = ['知网', '维普', '万方', '大雅', '格子达']

export const MARKETING_WORKFLOW_STEPS = [
  { icon: 'bi bi-ui-checks-grid', title: '配置任务', desc: '选择降重、降AIGC、双降或学术润色，并指定语言与目标检测体系' },
  { icon: 'bi bi-file-earmark-text', title: '提交文稿', desc: '粘贴段落试改，或上传 docx 全文；降AIGC 可附带官方检测报告' },
  { icon: 'bi bi-cpu', title: '智能处理', desc: '异步队列逐段语义重构，工作台实时展示进度与点数消耗' },
  { icon: 'bi bi-download', title: '交付成果', desc: '文本结果可复制对比，文件任务下载保留结构的 docx' }
]

export const MARKETING_FEATURES = [
  { icon: 'bi bi-arrow-repeat', title: '降重复率', desc: '在保持论点与术语的前提下重构表达，适配主流查重数据库策略。' },
  { icon: 'bi bi-robot', title: '降AIGC率', desc: '弱化机器生成痕迹，优化句式节奏与论证连贯性，便于自查与修改。' },
  { icon: 'bi bi-layers', title: '双降', desc: '链式执行降重与降AIGC，适用于查重率与 AI 率并重的定稿场景。' },
  { icon: 'bi bi-pencil-square', title: '学术润色', desc: '提升学术语体规范与逻辑层次，支持中英文，侧重语言质量而非检测数值。' }
]

export const MARKETING_HIGHLIGHTS = [
  { icon: 'bi bi-shield-lock', title: '文档隔离', desc: '加密传输，任务数据按账户隔离，仅用于完成您提交的处理' },
  { icon: 'bi bi-sliders', title: '检测体系适配', desc: '知网、维普、万方、大雅、格子达等策略可切换，与学校/期刊配置对齐' },
  { icon: 'bi bi-coin', title: '字符级计费', desc: '1 点 = 1 字符，提交前预览消耗，科研点数长期有效' },
  { icon: 'bi bi-arrow-repeat', title: '段落级复改', desc: '文件任务完成 7 日内，同一段落支持多次重新生成' },
  { icon: 'bi bi-list-check', title: '结构化处理', desc: '长文档按段改写，保留目录与版式，跳过不宜改动的要素' },
  { icon: 'bi bi-lightning-charge', title: '异步科研队列', desc: '提交即返回，长文档后台处理，支持断点续跑' }
]

export const MARKETING_COMPARE = {
  bad: {
    title: '泛用改写工具',
    items: [
      '同义词堆砌，论证链条断裂',
      '忽视检测体系差异，返工成本高',
      '仅支持短文本，长稿需反复粘贴',
      '排队不可预期，定稿周期被动延长'
    ]
  },
  good: {
    title: '笔芯科研',
    items: [
      '语义保真导向的段落级重构',
      '按知网/维普/万方等策略分别优化',
      'docx 全文上传，段落进度可追踪',
      '异步队列交付，适合科研写作节奏'
    ]
  }
}

export const MARKETING_FAQS = [
  {
    q: '笔芯科研适合哪些科研写作场景？',
    a: '面向学位论文定稿、课程报告、期刊投稿前自查与修改等场景。平台提供降重复率、降AIGC率、双降与学术润色，并支持AIGC检测、文献检索、答辩 PPT 辅助等扩展能力。请在遵守学校与期刊规范的前提下使用，处理效果因原文与检测版本而异。'
  },
  {
    q: '降重与降AIGC的策略有何不同？',
    a: '降重侧重与已有文献的重复表达重构，在保持原意基础上降低查重率；降AIGC侧重弱化机器生成特征，优化语序与论证方式。二者检测模型不同，提交时请选择与学校或编辑部一致的检测体系（知网、维普、万方等）。'
  },
  {
    q: '双降适合什么时候使用？',
    a: '当同一文稿需同时通过查重与AIGC检测时，可选用双降一次性完成两项处理，点数约为字数的 2 倍。若报告已明确重复段与 AI 段，也可分步处理以便精细控制。'
  },
  {
    q: '支持哪些提交方式？',
    a: '支持粘贴段落与上传docx。段落模式便于试改与对照；文件模式按段处理并回写 Word 结构。降AIGC可上传官方 PDF/Word 检测报告，优先处理标红或高 AI 率段落。'
  },
  {
    q: '学术润色与降重服务如何区分？',
    a: '学术润色面向语言质量：术语准确、句式规范、逻辑连贯，仅需选择中文或英文。降重/降AIGC/双降面向检测指标优化，需选择目标平台。润色不承诺查重率下降，降重也不等同于语言润色。'
  },
  {
    q: '科研点数如何计费？',
    a: '充值后进入统一科研点数账户，1 点 = 1 字符。降重、降 AIGC、润色一般为 1 倍字数，双降为 2 倍；提交前工作台显示预计消耗。点数长期有效，无订阅月费。'
  },
  {
    q: '文稿安全与隐私如何保障？',
    a: '文档加密传输，仅用于当次任务，不用于对外公开或模型训练。历史记录仅登录用户本人可见。建议在文稿中避免无关敏感信息，处理完成后及时下载并管理任务记录。'
  },
  {
    q: '处理时长与结果复改规则？',
    a: '任务进入异步队列，耗时与文稿长度及队列负载相关，可在任务页查看段落进度。文件类任务完成后 7 日内，同一段落支持重新生成，便于在可控成本下微调表述。'
  },
  {
    q: '如何理解知网、维普等系统的 AIGC 判定规则？',
    a: '各平台会综合句式模板、词汇分布、段落连贯性等特征给出 AI 疑似度，且规则会随版本更新。提交任务时请选择与学校或期刊一致的检测体系；改写后务必用同一版本官方渠道复检。笔芯科研按策略适配优化方向，不对某一版本阈值作固定承诺。'
  },
  {
    q: '处理后 Word 格式错乱或样式变化怎么办？',
    a: '全文 docx 任务以保留结构为目标，但复杂公式、域代码、嵌套文本框等场景仍建议处理前备份原稿。若个别段落样式异常，可在 Word 中使用「清除格式」后套用原样式，或在 7 日免费复改期内对该段重新生成。图表、题注等通常按规则跳过改写。'
  },
  {
    q: '充值或处理异常如何联系售后？',
    a: '支付与点数到账问题请保留订单截图，通过页脚邮箱联系客服；处理失败且点数已扣减的情况，可按平台规则核查队列日志后处理。学术效果因原文差异较大，一般不提供因个人主观不满意而全额退款，具体以用户协议与支付页说明为准。'
  }
]

export const MARKETING_FAQ_SECTIONS = [
  {
    id: 'intro',
    title: '平台介绍',
    desc: '笔芯科研聚焦学术文档智能处理：降重、降AIGC、双降、润色及科研扩展工具，科研点数统一计费。'
  },
  {
    id: 'workflow',
    title: '使用流程',
    desc: '配置任务 → 提交文稿 → 智能处理 → 交付成果，全程在线完成。'
  },
  {
    id: 'compliance',
    title: '合规说明',
    desc: '工具定位、学术规范与使用边界。'
  },
  {
    id: 'faq',
    title: '常见问题',
    desc: '检测适配、AIGC 规则、计费规则、文档安全与售后说明。'
  }
]
